<script setup lang="ts">
import { EButton, EDropdownMenu, EIcon, formatBinding, GLOBAL_SHORTCUTS, type MenuItem } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import LocaleToggle from '@/components/LocaleToggle.vue'
import ThemeToggle from '@/components/ThemeToggle.vue'
import { useAuthStore } from '@/stores/auth'

defineProps<{ navCollapsed: boolean }>()

const emit = defineEmits<{ toggleNav: []; openPalette: []; openShortcuts: [] }>()

const auth = useAuthStore()
const router = useRouter()
const { t } = useI18n()

const paletteHint = computed(() => formatBinding(GLOBAL_SHORTCUTS.commandPalette))

const menuItems = computed<MenuItem[]>(() => [
  { id: 'shortcuts', label: t('shell.menu.shortcuts'), shortcut: '?' },
  { id: 'logout', label: t('shell.menu.signOut'), destructive: true },
])

async function onMenuSelect(item: MenuItem): Promise<void> {
  if (item.id === 'shortcuts') emit('openShortcuts')
  if (item.id === 'logout') {
    await auth.logout()
    await router.push({ name: 'login' })
  }
}
</script>

<template>
  <header
    class="sticky top-0 z-10 flex h-12 items-center gap-3 border-b border-subtle bg-raised px-3"
  >
    <EButton
      variant="ghost"
      size="sm"
      icon-only
      :aria-label="navCollapsed ? $t('shell.expandNav') : $t('shell.collapseNav')"
      @click="emit('toggleNav')"
    >
      <EIcon :name="navCollapsed ? 'chevronsRight' : 'chevronsLeft'" :size="16" />
    </EButton>

    <RouterLink to="/" class="flex items-center gap-2 rounded px-1 text-sm font-semibold">
      <span
        class="grid h-6 w-6 place-items-center rounded bg-accent text-2xs font-bold text-on-accent"
        aria-hidden="true"
      >AD</span>
      <!-- The mark is decorative and the product name is a proper noun; neither is prose. -->
      <span>erpx <span class="text-muted font-normal">{{ $t('shell.brandSuffix') }}</span></span>
    </RouterLink>

    <button
      type="button"
      class="ms-4 flex h-8 min-w-64 flex-1 items-center gap-2 rounded border border-subtle bg-sunken px-2 text-start text-sm text-muted hover:border-line"
      @click="emit('openPalette')"
    >
      <EIcon name="search" :size="14" />
      <span class="flex-1">{{ $t('shell.searchButton') }}</span>
      <kbd class="rounded-sm border border-subtle px-1 text-2xs">{{ paletteHint }}</kbd>
    </button>

    <LocaleToggle />
    <ThemeToggle />

    <EDropdownMenu :items="menuItems" :label="$t('common.labels.account')" @select="onMenuSelect">
      <template #trigger>
        <EButton variant="ghost" size="sm">
          <span class="max-w-40 truncate">
            {{ auth.user?.name ?? auth.user?.email ?? $t('common.labels.account') }}
          </span>
          <EIcon name="chevronDown" :size="14" />
        </EButton>
      </template>
    </EDropdownMenu>
  </header>
</template>
